import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AddPropertyData,
  AgentPortfolio,
  BaseInterface,
  ContactAgent,
  ContactUsData,
  CreateOffMarketProperty,
  DocumentUploadResponse,
  ExtendedBaseInterface,
  ISearchRequest,
  MapPropertySearchParams,
  MarketedPropertyListing,
  MessagesList,
  MessagesRequest,
  MlsPropertyDetails,
  NewsletterSubscribe,
  OffMarketProperty,
  PayrollDocuments,
  PayrollSubmissions,
  ListingPaymentInitiateRequest,
  ListingPaymentInitiateResponse,
  PayPalExecutePaymentRequest,
  KycIdentitySessionResponse,
  PropertySearch,
  ScheduleTour,
  SchedulTourStatus,
  SendMessageRequest,
  TicketReplyData,
} from 'src/app/shared/models';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private http: HttpClient) {}

  getUserProperties(data: ISearchRequest) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Property/Listing/GetListings`,
      data,
    );
  }

  getMarketedProperties(data: ISearchRequest) {
    return this.http.post<BaseInterface>(
      `${environment.baseUrl}MarketedProperty/GetListings?PageNumber=${data.PageNumber}&PageSize=${data.PageSize}&SortColumn=${data.SortColumn}&SortOrder=${data.SortOrder}&Search=${data.Search}`,
      '',
    );
  }

  getMapProperties(data: Partial<MapPropertySearchParams>) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Property/Mls/map-markers`,
      data,
    );
  }

  getUserWishList(data: ISearchRequest) {
    return this.http.get<ExtendedBaseInterface>(
      `${environment.baseUrl}Wishlist?PageNumber=${data.PageNumber}&PageSize=${data.PageSize}&SortColumn=${data.SortColumn}&SortOrder=${data.SortOrder}&Search=${data.Search}`,
    );
  }

  addPropertyToWishList(listingId: string) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Wishlist/add/${listingId}`,
      '',
    );
  }

  removePropertyFromWishList(listingId: number) {
    return this.http.delete<ExtendedBaseInterface>(
      `${environment.baseUrl}Wishlist/remove/${listingId}`,
    );
  }

  getAddedPropertyInWishList() {
    return this.http.get<ExtendedBaseInterface>(
      `${environment.baseUrl}Wishlist/GetUserWishlistPropertyIds`,
    );
  }

  addProperty(data: FormData) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Property/Listing/CreateListing`,
      data,
    );
  }

  addMarketedProperty(data: MarketedPropertyListing) {
    return this.http.post<MarketedPropertyListing>(
      `${environment.baseUrl}MarketedProperty/CreateListing`,
      data,
    );
  }

  submitPropertyForApproval(id: number) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}MarketedProperty/SubmitForApproval/${id}`,
      '',
    );
  }

  removeUserProperty(listingId: number) {
    return this.http.delete<ExtendedBaseInterface>(
      `${environment.baseUrl}MarketedProperty/DeleteListing/${listingId}`,
    );
  }

  markAsSold(id: number) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}MarketedProperty/MarkAsSold/${id}`,
      '',
    );
  }

  initiateMarketedListingPayment(data: ListingPaymentInitiateRequest) {
    const endpoint =
      data.paymentMethod === 'stripe'
        ? 'ListingPayment/stripe/initiate'
        : 'ListingPayment/paypal/initiate';

    return this.http.post<ListingPaymentInitiateResponse>(
      `${environment.baseUrl}${endpoint}`,
      data,
    );
  }

  executePayPalListingPayment(data: PayPalExecutePaymentRequest) {
    return this.http.post<BaseInterface>(
      `${environment.baseUrl}ListingPayment/paypal/execute`,
      data,
    );
  }

  startKycIdentitySession() {
    return this.http.post<KycIdentitySessionResponse>(
      `${environment.baseUrl}Kyc/stripe/identity/create-session`,
      {},
    );
  }

  getUserPropertyDetails(propertyId: string) {
    return this.http.get<ExtendedBaseInterface>(
      `${environment.baseUrl}MarketedProperty/Listing/${propertyId}`,
    );
  }

  getArisMarketedPropertyDetails(propertyId: number) {
    return this.http.get<ExtendedBaseInterface>(
      `${environment.baseUrl}MarketedProperty/Public/Listing/${propertyId}`,
    );
  }

  getUserTickets(data: ISearchRequest, role: 'Agent' | 'User') {
    return this.http.get<ExtendedBaseInterface>(
      `${environment.baseUrl}Ticket/${
        role === 'Agent' ? 'GetAgentTickets' : 'GetTickets'
      }?PageNumber=${data.PageNumber}&PageSize=${data.PageSize}&SortColumn=${
        data.SortColumn
      }&SortOrder=${data.SortOrder}&Search=${data.Search}`,
    );
  }

  createUserTicket(data: FormData) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Ticket/CreateTicket`,
      data,
    );
  }

  deleteUserTicket(id: number) {
    return this.http.delete<ExtendedBaseInterface>(
      `${environment.baseUrl}Ticket/DeleteTicket?id=${id}`,
    );
  }

  getTicketDetails(id: number) {
    return this.http.get<ExtendedBaseInterface>(
      `${environment.baseUrl}Ticket/${id}`,
    );
  }

  updateUserTicketStatus(id: number) {
    const data = {
      status: 3,
    };
    return this.http.patch<ExtendedBaseInterface>(
      `${environment.baseUrl}Ticket/UpdateTicketStatus/?id=${id}`,
      data,
    );
  }

  getTicketReplies(id: number) {
    return this.http.get<ExtendedBaseInterface>(
      `${environment.baseUrl}Ticket/GetReplies/?ticketId=${id}`,
    );
  }

  addTicketReply(data: TicketReplyData) {
    const formData = new FormData();
    formData.append('Ticketid', JSON.stringify(data.ticketId));
    formData.append('Message', data.message);
    formData.append('Image', data.image);
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Ticket/AddReply`,
      formData,
    );
  }

  searchMlsProperties(data: PropertySearch) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Property/Mls/SearchProperty`,
      data,
    );
  }

  getMlsPropertyDetails(listingKey: string) {
    return this.http.get<MlsPropertyDetails>(
      `${environment.baseUrl}Property/Mls/${listingKey}`,
    );
  }

  scheduleATour(data: ScheduleTour) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Home/ScheduleTour`,
      data,
    );
  }

  scheduleMarketedPropertyTour(data: ScheduleTour) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Home/Marketed/ScheduleTour`,
      data,
    );
  }

  getUserScheduledTours(data: ISearchRequest) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Home/GetUserScheduledTours?PageNumber=${data.PageNumber}&PageSize=${data.PageSize}&SortColumn=${data.SortColumn}&SortOrder=${data.SortOrder}&Search=${data.Search}`,
      '',
    );
  }

  //agent
  getAgentLeads(data: ISearchRequest) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Home/GetAgentLeads?PageNumber=${data.PageNumber}&PageSize=${data.PageSize}&SortColumn=${data.SortColumn}&SortOrder=${data.SortOrder}&Search=${data.Search}`,
      '',
    );
  }

  getMarketedAgentLeads(data: ISearchRequest) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Home/Marketed/GetAgentLeads?PageNumber=${data.PageNumber}&PageSize=${data.PageSize}&SortColumn=${data.SortColumn}&SortOrder=${data.SortOrder}&Search=${data.Search}`,
      '',
    );
  }

  updateAgentLeadStatus(id: number) {
    return this.http.patch<ExtendedBaseInterface>(
      `${environment.baseUrl}Home/UpdateLeadStatus?id=${id}&status=${SchedulTourStatus.completed}`,
      '',
    );
  }

  //contact us
  contactUs(data: ContactUsData) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Home/ContactUs`,
      data,
    );
  }

  getAgentAppointments(data: ISearchRequest) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Home/GetAgentAppointments?PageNumber=${data.PageNumber}&PageSize=${data.PageSize}&SortColumn=${data.SortColumn}&SortOrder=${data.SortOrder}&Search=${data.Search}`,
      '',
    );
  }
  updateAgentAppointmentStatus(id: number) {
    return this.http.patch<ExtendedBaseInterface>(
      `${environment.baseUrl}Home/UpdateAppointmentStatus?id=${id}&status=${SchedulTourStatus.sold}`,
      '',
    );
  }

  //Messages
  getMessagesList(userId: string) {
    return this.http.get<MessagesList[]>(
      `${environment.baseUrl}Messages/GetUsersWithChat?userId=${userId}`,
    );
  }

  getMessages(data: MessagesRequest) {
    return this.http.get<SendMessageRequest>(
      `${environment.baseUrl}Messages/${data.senderId}/${data.recieverId}`,
    );
  }

  sendMessage(data: SendMessageRequest) {
    return this.http.post<BaseInterface>(
      `${environment.baseUrl}Messages/SendMessage`,
      data,
    );
  }

  getUserById(id: number) {
    return this.http.get<BaseInterface>(
      `${environment.baseUrl}Admin/GetUserDetailsById?id=${id}`,
    );
  }

  getBlogs(data: ISearchRequest) {
    return this.http.get<ExtendedBaseInterface>(
      `${environment.baseUrl}Blog/GetAllBlogs?PageNumber=${data.PageNumber}&PageSize=${data.PageSize}&SortColumn=${data.SortColumn}&SortOrder=${data.SortOrder}&Search=${data.Search}`,
    );
  }

  getBlogById(id: number) {
    return this.http.get<BaseInterface>(
      `${environment.baseUrl}Blog/GetBlog?Id=${id}`,
    );
  }

  registerAgent(data: any) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Home/SubmitAgentApplication`,
      data,
    );
  }

  getAllRegisterAgents(data: ISearchRequest) {
    return this.http.get<ExtendedBaseInterface>(
      `${environment.baseUrl}Home/GetAllAgents?Search=${data.Search}`,
    );
  }

  getAgentById(id: number) {
    return this.http.get<BaseInterface>(
      `${environment.baseUrl}Home/GetAgentById/${id}`,
    );
  }

  getYoutubeVideos(max: number) {
    return this.http.get<BaseInterface>(
      `${environment.baseUrl}Socials/Youtube/Videos?maxResults=${max}`,
    );
  }

  getInstagramPosts(max: number) {
    return this.http.get<BaseInterface>(
      `${environment.baseUrl}Socials/Instagram/Posts?maxResults=${max}`,
    );
  }

  getTwitterPosts(data: any) {
    return this.http.get<BaseInterface>(
      `${environment.baseUrl}Socials/Twitter/Tweets?maxResults=${data.max}&PaginationToken=${data.token}`,
    );
  }

  newsletterSubscribe(data: NewsletterSubscribe) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Home/CreateSubscriber`,
      data,
    );
  }

  contactAgent(data: ContactAgent) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Home/ContactAgent`,
      data,
    );
  }

  getAgentContactQueries() {
    return this.http.get<ExtendedBaseInterface>(
      `${environment.baseUrl}Home/GetAgentContactQueries`,
    );
  }

  getAgentPortfolio() {
    let params = new HttpParams();
    params = params.set('ignoreIfError', '1');

    return this.http.get<ExtendedBaseInterface>(
      `${environment.baseUrl}Home/GetAgentPortfolio`,
      { params },
    );
  }

  updateAgentPortfolio(data: FormData) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}Home/UpdateAgentPortfolio`,
      data,
    );
  }

  uploadTeamMemberPhoto(file: File) {
    const formData = new FormData();
    formData.append('File', file, file.name);
    return this.http.post<BaseInterface>(
      `${environment.baseUrl}Home/AgentPortfolio/UploadImage`,
      formData,
    );
  }

  getAgentPayrollSubmissions(data: ISearchRequest) {
    return this.http.get<ExtendedBaseInterface>(
      `${environment.baseUrl}PayrollSubmissions/GetAllPayrollSubmissions?PageNumber=${data.PageNumber}&PageSize=${data.PageSize}&SortColumn=${data.SortColumn}&SortOrder=${data.SortOrder}&Search=${data.Search}`,
    );
  }

  getAgentPayrollSubmissionDetails(id: number) {
    return this.http.get<ExtendedBaseInterface>(
      `${environment.baseUrl}PayrollSubmissions/GetPayrollSubmission?id=${id}`,
    );
  }

  createAgentPayrollRequest(data: PayrollSubmissions) {
    return this.http.post<PayrollSubmissions>(
      `${environment.baseUrl}PayrollSubmissions/CreatePayrollSubmission`,
      data,
    );
  }

  updateAgentPayrollRequest(id: number, data: PayrollSubmissions) {
    return this.http.put<PayrollSubmissions>(
      `${environment.baseUrl}PayrollSubmissions/UpdatePayrollSubmission?id=${id}`,
      data,
    );
  }

  uploadAgentPayrollDocuments(data: PayrollDocuments) {
    const formData = new FormData();
    formData.append('File', data.file, data.file.name);
    formData.append('DocumentType', data.documentType);
    return this.http.post<{
      attachmentId: number;
      blobUrl: string;
      documentType: string;
    }>(
      `${environment.baseUrl}PayrollSubmissions/UploadAttachment?submissionId=${data.submissionId}`,
      formData,
    );
  }

  deleteAgentPayrollSubmission(submissionId: number) {
    return this.http.delete<ExtendedBaseInterface>(
      `${environment.baseUrl}PayrollSubmissions/DeletePayrollSubmission?submissionId=${submissionId}`,
    );
  }

  deleteAgentPayrollAttachment(attachmentId: number, submissionId: number) {
    return this.http.delete<ExtendedBaseInterface>(
      `${environment.baseUrl}PayrollSubmissions/DeleteAttachment?attachmentId=${attachmentId}&submissionId=${submissionId}`,
    );
  }

  getAgentOffMarketProperties(data: ISearchRequest) {
    return this.http.get<ExtendedBaseInterface>(
      `${environment.baseUrl}OffMarketProperties/List?PageNumber=${data.PageNumber}&PageSize=${data.PageSize}&SortColumn=${data.SortColumn}&SortOrder=${data.SortOrder}&Search=${data.Search}`,
    );
  }

  getAgentOffMarketPropertyDetails(id: number) {
    return this.http.get<OffMarketProperty>(
      `${environment.baseUrl}OffMarketProperties/GetOffMarket?id=${id}`,
    );
  }

  createAgentOffMarketProperty(data: CreateOffMarketProperty) {
    return this.http.post<OffMarketProperty>(
      `${environment.baseUrl}OffMarketProperties/CreateOffMarket`,
      data,
    );
  }

  updateAgentOffMarketProperty(data: OffMarketProperty) {
    return this.http.put<OffMarketProperty>(
      `${environment.baseUrl}OffMarketProperties/UpdateOffMarket`,
      data,
    );
  }

  deleteAgentOffMarketProperty(id: number) {
    return this.http.delete<ExtendedBaseInterface>(
      `${environment.baseUrl}OffMarketProperties/DeleteOffMarket?id=${id}`,
    );
  }

  uploadAgentOffMarketPropertyDocuments(data: {
    file: File;
    order: number;
    propertyId: number;
  }) {
    const formData = new FormData();
    formData.append('File', data.file, data.file.name);
    formData.append('Order', data.order.toString());
    return this.http.post<DocumentUploadResponse>(
      `${environment.baseUrl}OffMarketProperties/UploadImage?propertyId=${data.propertyId}`,
      formData,
    );
  }

  deleteAgentOffMarketPropertyDocument(data: {
    propertyId: number;
    imageId: number;
  }) {
    return this.http.delete<ExtendedBaseInterface>(
      `${environment.baseUrl}OffMarketProperties/DeleteImage?propertyId=${data.propertyId}&imageId=${data.imageId}`,
    );
  }

  generateAgentOffMarketPropertyShareLink(propertyId: number) {
    return this.http.post<{
      token: string;
      createdAtUtc: string;
      expiresAtUtc: string;
      isRevoked: boolean;
    }>(
      `${environment.baseUrl}OffMarketProperties/GenerateShareLink?propertyId=${propertyId}`,
      '',
    );
  }

  getSharedOffMarketProperty(token: string) {
    return this.http.get<OffMarketProperty>(
      `${environment.baseUrl}OffMarketProperties/Shared/${token}/?token=${token}`,
    );
  }

  getArisMarketedProperties(data: PropertySearch) {
    return this.http.post<ExtendedBaseInterface>(
      `${environment.baseUrl}MarketedProperty/SearchProperty`,
      data,
    );
  }

  getArisMarketedMapProperties(data: Partial<MapPropertySearchParams>) {
    return this.http.post<MarketedPropertyListing[]>(
      `${environment.baseUrl}MarketedProperty/map-markers`,
      data,
    );
  }
}
